import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AccordeonMail } from './StageParametrageContacts';
import { stageAPI } from '../services/api';
import { presentationDe } from '../services/modelesCourrielPresentation';
import './SuiviStage.css';

/**
 * Le geste pour les envois du STAGE (Tuteurs, CFA ; Stage et Certification ensuite) — lot MC.2.
 * `rePreparer(typeMail)` refait le brouillon de l'envoi (la route qui l'a fabriqué) : après un enregistrement, le texte
 * recomposé par le serveur revient dans la fenêtre. Le modèle ouvert est celui QUE L'ENVOI UTILISE (route
 * GET /stage/modeles-mail/:type : la copie du processus si elle existe, sinon le département) ; l'éditeur écrit au même
 * endroit. Rend `onModeles` (à passer à la fenêtre d'envoi, null si le type n'a pas d'éditeur) et `fenetre` (à rendre).
 */
export function useModelesEnvoiStage({ departementId, typeMail, rePreparer, configurationId = null, certifProcessusId = null }) {
    const [editeur, setEditeur] = useState(null);       // { typeMail, resoudre }
    const portee = useRef(null);                        // { configurationId, certifProcessusId, portee }
    const presentation = typeMail ? presentationDe(typeMail) : null;
    const onModeles = useCallback((t) => new Promise((resoudre) => setEditeur({ typeMail: t, resoudre })), []);
    const lie = (fn) => (...args) => fn(...args, portee.current?.configurationId || null, portee.current?.certifProcessusId || null);
    const api = {
        listerVersionsMail: lie(stageAPI.listerVersionsMail),
        enregistrerModeleMail: lie(stageAPI.enregistrerModeleMail),
        restaurerVersionMail: lie(stageAPI.restaurerVersionMail)
    };
    const fermer = async (change) => {
        const { typeMail: t, resoudre } = editeur;
        setEditeur(null);
        if (!change) { resoudre(null); return; }
        try { const b = await rePreparer(t); resoudre(b ? { sujet: b.sujet, html: b.html } : null); } catch (e) { resoudre(null); }
    };
    const fenetre = editeur ? (
        <FenetreModeleCourriel departementId={departementId} modifiable api={api}
            charger={async () => {
                const r = (await stageAPI.modeleMailEnvoi(editeur.typeMail, departementId, configurationId, certifProcessusId)).data;
                portee.current = r;
                return r.modele;
            }}
            presentation={{ ...presentationDe(editeur.typeMail), note: presentationDe(editeur.typeMail)?.noteLien,
                            // calculée d'après le modèle CHARGÉ : la portée n'est connue qu'après la lecture
                            portee: (m) => (m.configuration_id ? 'Ce modèle est celui du processus de suivi de cet envoi, avec ses versions.'
                                : m.certif_processus_id ? 'Ce modèle est celui du processus de certification de cet envoi, avec ses versions.'
                                : 'Ce modèle est celui du département, gardé d’un envoi à l’autre, avec ses versions.') }}
            onFermer={fermer} />
    ) : null;
    return { onModeles: presentation && rePreparer ? onModeles : null, fenetre };
}

/**
 * « MODÈLES DE COURRIEL… » — l'éditeur d'UN modèle, ouvert depuis une fenêtre d'envoi (Bertrand, 2026-10-28 : le bouton
 * vu au Planning annuel, à généraliser à toutes les fenêtres d'envoi). C'est l'éditeur habituel (AccordeonMail : objet,
 * corps, variables, copies, versions, texte d'origine), posé dans une fenêtre.
 *
 *  · `charger()` → le modèle (ligne de `stages_modeles_mail`) — celui qui sert À CET ENVOI : du département (Planning
 *    annuel, Planning oraux, Tuteurs, CFA) ou du PROCESSUS concerné (Stage, Certification) ;
 *  · `api` : l'API des modèles que l'éditeur appelle (listerVersionsMail, enregistrerModeleMail, restaurerVersionMail),
 *    déjà liée au bon processus le cas échéant ;
 *  · `presentation` : ce que l'éditeur montre (variables, destinataire, icône, sous-titre, note, copies, options) ;
 *  · `onFermer(change)` : change = le modèle a été enregistré ou restauré pendant l'ouverture.
 */
export default function FenetreModeleCourriel({ departementId, modifiable, api, charger, presentation, onFermer }) {
    const [modele, setModele] = useState(null);
    const [erreur, setErreur] = useState(null);
    const change = useRef(false);
    useEffect(() => {
        let vivant = true;
        charger()
            .then((m) => { if (vivant) { if (m) setModele(m); else setErreur('Ce modèle de courriel est introuvable dans ce département.'); } })
            .catch((e) => { if (vivant) setErreur(e?.response?.data?.error || e?.message || 'Le modèle ne se charge pas.'); });
        return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const p = presentation || {};
    const fermer = () => onFermer(change.current);
    return (
        <div className="stg-root fmc-hote">
            <div className="stg-modale-fond fmc-fond" role="dialog" aria-modal="true" onClick={fermer}>
                <div className="stg-modale" style={{ maxWidth: 900, width: '92vw' }} onClick={(e) => e.stopPropagation()}>
                    <div className="stg-modale-header"><i className="fa-solid fa-pen" /> <span>Modèle de courriel — {p.destinataire || modele?.libelle || ''}</span></div>
                    <div className="stg-modale-body">
                        {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span>{erreur}</span></div>}
                        {!modele && !erreur && <div className="fe-aide"><i className="fa-solid fa-circle-notch stg-tourne" /> Chargement…</div>}
                        {modele && (
                            <AccordeonMail modele={modele} ouvert onBasculer={() => {}} departementId={departementId} modifiable={modifiable}
                                onMiseAJour={({ modele: neuf }) => { change.current = true; if (neuf) setModele(neuf); }} api={api}
                                variables={p.variables} destinataire={p.destinataire} icone={p.icone} sousTitre={p.sousTitre} noteLien={p.note}
                                noteDestinataires={p.noteDestinataires} ccSupplementaires={p.ccSupplementaires || []} options={p.options || []}
                                libellesCc={p.libellesCc || null} />
                        )}
                        <div className="fe-aide"><i className="fa-solid fa-circle-info" /> {(typeof p.portee === 'function' ? (modele ? p.portee(modele) : '') : p.portee) || 'Le modèle est gardé d\'un envoi à l\'autre, avec ses versions.'} Enregistré, il remplace le texte de la fenêtre d'envoi (on vous demande si vous l'aviez retouché).</div>
                    </div>
                    <div className="stg-modale-pied"><button type="button" className="btn btn-green btn-sm" onClick={fermer}>Fermer</button></div>
                </div>
            </div>
        </div>
    );
}
